import React, { useEffect, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

// Components
import ConquestIntegrationLayout from './ConquestIntegrationLayout';
import ImportAccountsEmptyPage from './partials/ImportAccountsEmptyState';
import LoadingScreen from '@/views/components/common/LoadingScreen';
import ContactDetailsInfo from '../contacts/contact-details/ContactDetailsInfo';
import { PlanAccountsTable } from './data-tables/PlanAccountsTable';

// RTK Query hooks
import { useGetPlanQuery, useUpdatePlanMutation } from '@/redux/api/planning';
import { getContactById } from '@/redux/modules/contacts';

// Types
import { Account, Plan } from '@/types/planning';
import Nullable from '@/types/nullable';
import { SearchInput } from '@/views/components/inputs';
import { ReduxState } from '@/redux/modules/types';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';

// Utils
import { formatMessage, LOCALE } from '@/utils/i18n';
import { useClientSideSearch } from '@/hooks/useClientSideSearch';
import { ImportAccountsFooter } from './partials/ImportAccountsFooter';
import { AccountColumns } from './data-tables/plan-accounts-table/useAccountColumnsDefinition';
import { filterImportedAccounts, filterValidAccounts } from '@/utils/planning';
import notification from '@/utils/notification';
import { Dispatch } from '@/redux';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

interface MatchParams {
  planId: string;
}

type Props = { user: User } & RouteComponentProps<MatchParams>;
const i18nBasePath = 'Planning.Conquest.ImportAccountsPage';

const ImportAccountsPage: React.FC<Props> = ({ match: url, user }: Props) => {
  const locale = useSelector((state: ReduxState) => state.intl.locale);
  const { tableColumns, contact, hasAccounts, plan, isLoading, isContactLoading } = useData({ url });
  const { onClose, onImportAccounts, onSelectAccounts, nbSelectedAccounts, isUpdating } = useActions({ plan, contactId: contact?._id });
  const { searchQuery, setQuery, filteredData } = useSearch({ plan: plan || null, locale: locale as LOCALE });

  const LoaderComponent = isUpdating
    ? () => <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.UpdatePlan.Title`} />
    : isContactLoading
      ? () => <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.GetContact.Title`} />
      : () => <LoadingScreen titleToLocalize={`${i18nBasePath}.Loading.GetPlan.Title`} />;

  return (
    <ConquestIntegrationLayout
      FooterComponent={
        <ImportAccountsFooter
          onImportAccounts={onImportAccounts}
          isDisabled={nbSelectedAccounts === 0}
          i18nBasePath={i18nBasePath}
          nbSelectedAccounts={nbSelectedAccounts}
        />
      }
      LoaderComponent={LoaderComponent}
      mainClassName="planning-import-accounts-page"
      onClose={onClose}
      i18nBasePath={i18nBasePath}
      isLoading={isLoading || isContactLoading || isUpdating}
    >
      {contact && <ContactDetailsInfo user={user} hasDisplayedActions={false} isGIF={false} locale={locale as LOCALE} contact={contact} />}

      <div className="content-section">
        <div className="section-header">
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
            {formatMessage(`${i18nBasePath}.TableSectionTitle`)}
          </Typography>
          <SearchInput className="search-input-v2" onChange={setQuery} />
        </div>
        <ImportAccountsEmptyPage hasAccounts={hasAccounts} />
        {hasAccounts && plan && (
          <div className="table-container">
            <PlanAccountsTable
              columns={tableColumns}
              data={filteredData}
              i18nBasePath={i18nBasePath}
              searchQuery={searchQuery}
              isImportPage
              isSelectable
              onRowSelectionChange={onSelectAccounts}
            />
          </div>
        )}
      </div>
    </ConquestIntegrationLayout>
  );
};

const useSearch = ({ plan, locale }: { plan: Nullable<Plan>; locale: LOCALE }) => {
  const [query, setQuery] = useState<string>('');
  const searchableFields = ['name', `riskProfile.title.${locale}`, 'totalValue', 'targetRateOfReturn', 'owner'];
  const { filteredData } = useClientSideSearch({ data: plan?.accounts || [], query, searchableFields });
  return {
    searchQuery: query,
    setQuery,
    filteredData,
  };
};

const useActions = ({ plan, contactId }: { contactId?: string; plan?: Plan }) => {
  const history = useHistory();
  const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([]);
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();

  const onClose = () => {
    if (!contactId) {
      history.push('/contacts');
    }
    history.push(`/contacts/${contactId}/plan-accounts`);
  };

  const onImportAccounts = async () => {
    if (!plan?._id || !contactId) {
      return;
    }

    const currentAccountIds = filterValidAccounts(filterImportedAccounts(plan.accounts)).map(account => account._id) || [];
    const accountIds = selectedAccounts.map(account => account._id);
    const mergedAccountIds = [...currentAccountIds, ...accountIds];

    try {
      await updatePlan({
        planId: plan._id,
        contactId,
        payload: { importedAccountIds: mergedAccountIds } as any,
      }).unwrap();
      history.push(`/contacts/${plan.contactId}/plan-accounts`);
    } catch (error) {
      console.error('Failed to update plan:', error);
    }
  };

  const onSelectAccounts = (selectedAccounts: Account[]) => {
    setSelectedAccounts(selectedAccounts);
  };

  return {
    nbSelectedAccounts: selectedAccounts.length,
    onClose,
    onImportAccounts,
    onSelectAccounts,
    isUpdating,
  };
};

type DataProps = {
  url: { params: { planId: string } };
};
const useData = ({ url }: DataProps) => {
  const tableColumns = [AccountColumns.NAME, AccountColumns.TOTAL_VALUE, AccountColumns.RISK_PROFILE, AccountColumns.TARGET_RATE_OF_RETURN];
  const history = useHistory();
  const dispatch = useDispatch<Dispatch>();
  const { planId } = url.params;

  const [contact, setContact] = useState<Nullable<GenericContact>>(null);
  const [isContactLoading, setIsContactLoading] = useState<boolean>(false);
  const { data: plan, isLoading, error } = useGetPlanQuery(planId);
  const hasAccounts = !!plan?.accounts && plan.accounts.length > 0;

  useEffect(() => {
    const init = async () => {
      if (error) {
        notification.error(formatMessage(`${i18nBasePath}.Notification.Error.FetchPlanAccounts.Message`));
        return history.push('/');
      }

      if (!plan) {
        return; // Still loading
      }

      if (!plan.contactId) {
        return history.push(`/planning/${planId}/assign-contact`);
      }

      try {
        setIsContactLoading(true);
        const contactData = await dispatch(getContactById(plan.contactId));
        setContact(contactData);
      } catch (e) {
        notification.error(formatMessage(`${i18nBasePath}.Notification.Error.FetchContact.Message`));
        return history.push('/');
      } finally {
        setIsContactLoading(false);
      }
    };

    init();
  }, [plan, error, planId, dispatch]);

  return {
    tableColumns,
    contact,
    hasAccounts,
    plan,
    isLoading,
    isContactLoading,
  };
};

export default ImportAccountsPage;
